// Single source of truth for a speaker's lifecycle status, shared by the
// overview counts and the workspace list badge so they can't drift apart.
// `key` is the stable value used for filtering; `id` is the i18n message id.
// A speaker has ONE durable profile but can be invited to MULTIPLE events
// (profile.engagements); the lifecycle status is derived from that list.
export const SPEAKER_STATUS_KEYS = [
  'researching', 'needsReview', 'readyToInvite', 'invited', 'signedUp', 'confirmed', 'failed', 'notQueued',
];

export const statusMeta = p => {
  const engagements = p.engagements || [];
  // Once invited, the invitation lifecycle is the salient state, ahead of any
  // (re-triggered) research job.
  if (engagements.length > 0) {
    if (p.confirmedAt) return { key: 'confirmed', id: 'speakerMgmt.status.confirmed', def: 'Confirmed', bg: '#dcfce7', fg: '#166534' };
    if (p.accountStatus === 'ACTIVE') return { key: 'signedUp', id: 'speakerMgmt.status.signedUp', def: 'Signed up', bg: '#e0f2fe', fg: '#075985' };
    return { key: 'invited', id: 'speakerMgmt.status.invited', def: 'Invited', bg: '#ede9fe', fg: '#5b21b6' };
  }
  const e = p.enrichmentStatus;
  if (e === 'PENDING' || e === 'RUNNING') return { key: 'researching', id: 'speakerMgmt.status.researching', def: 'Researching…', bg: '#fef3c7', fg: '#92400e' };
  if (e === 'FAILED') return { key: 'failed', id: 'speakerMgmt.status.failed', def: 'Research failed', bg: '#fee2e2', fg: '#991b1b' };
  if (e === 'NEEDS_REVIEW') return { key: 'needsReview', id: 'speakerMgmt.status.needsReview', def: 'Needs review', bg: '#e0f2fe', fg: '#075985' };
  // Research has been reviewed (fields applied/kept) but this speaker hasn't
  // been invited to any event yet - distinct from "notQueued" so a finished,
  // invitable draft doesn't read as if nothing had happened.
  if (e === 'REVIEWED') return { key: 'readyToInvite', id: 'speakerMgmt.status.readyToInvite', def: 'Ready to invite', bg: '#dcfce7', fg: '#166534' };
  return { key: 'notQueued', id: 'speakerMgmt.status.notQueued', def: 'Research not started', bg: '#f1f5f9', fg: '#475569' };
};

// True when ANY of the speaker's engagements has a pending title change request.
export const hasPendingTitleChange = p => (p.engagements || []).some(e => e.talkTitleSuggestion);

// True when ANY of the speaker's engagements has slides the admin hasn't
// acknowledged yet - set (or re-cleared) on every new/replaced upload.
export const hasPendingSlidesUpload = p => (p.engagements || []).some(e => e.slidesUrl && !e.slidesAcknowledgedAt);

// True when the speaker has been invited to at least one event but hasn't
// confirmed their attendance yet.
export const hasNotAcceptedInvite = p => (p.engagements || []).length > 0 && !p.confirmedAt;

// True when the speaker has at least one engagement that actually requires a
// presentation and doesn't have slides yet (as opposed to
// hasPendingSlidesUpload, which is about uploads the admin hasn't reviewed
// yet). An engagement the admin flagged as not needing one (see
// requiresPresentation on invite) never counts as missing.
export const hasNoSlidesUploaded = p => (p.engagements || []).some(e => e.requiresPresentation !== false && !e.slidesUrl);

// A profile can be invited (first time or to another event) once it has been
// through AI research and has a usable email - not while research is still
// running, failed outright, or was never queued (so there are no reviewed
// fields to show the speaker yet). Once invited at least once, engagements
// being non-empty already implies a reviewed profile.
export const canInvite = p => !!p.email && ['needsReview', 'readyToInvite', 'invited', 'signedUp', 'confirmed'].includes(statusMeta(p).key);
